import { ComponentProps, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { cn, ToggleGroup, ToggleGroupItem } from '@sib/ui';
import { FormField } from './BaseFormComponents';
import { BaseFormFieldWrapper } from './BaseFormFieldWrapper';
import { FormComponentBaseProps } from './types/form-base-props';

export interface Option {
  label: string | ReactNode;
  value: string;
  className?: string;
  icon?: ReactNode;
}

export type FormToggleGroupProps = FormComponentBaseProps &
  ComponentProps<typeof ToggleGroup> & {
    options: Option[];
    groupItemsProps?: Partial<ComponentProps<typeof ToggleGroupItem>>;
    allowNullValue?: boolean;
  };

const FormToggleGroup = ({
  description,
  descriptionProps,
  label,
  labelProps,
  hideError,
  defaultValue,
  name,
  disabled,
  allowNullValue,
  options,
  groupItemsProps,
  ...props
}: FormToggleGroupProps) => {
  const { control, formState } = useFormContext();
  const { isSubmitting } = formState;

  return (
    <FormField
      shouldUnregister={false}
      disabled={disabled}
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field }) => (
        <BaseFormFieldWrapper
          description={description}
          descriptionProps={descriptionProps}
          label={label}
          labelProps={labelProps}
          hideError={hideError}
        >
          <ToggleGroup
            {...props}
            onValueChange={(val: Option['value'] | Option['value'][]) => {
              if (val) {
                field.onChange(val);

                return;
              }

              if (allowNullValue) field.onChange(val);
            }}
            {...field}
          >
            {options.map(({ value, label, className, icon }) => (
              <ToggleGroupItem
                {...groupItemsProps}
                disabled={disabled || isSubmitting || groupItemsProps?.disabled}
                className={cn(
                  'flex flex-row gap-2',
                  groupItemsProps?.className,
                  className
                )}
                key={value}
                value={value}
              >
                {icon && <span>{icon}</span>}
                <span>{label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </BaseFormFieldWrapper>
      )}
    />
  );
};

export { FormToggleGroup };
